import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [fpOpen, setFpOpen] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fpUser, setFpUser] = useState("");
  const [fpQuestion, setFpQuestion] = useState("");
  const [fpAnswer, setFpAnswer] = useState("");
  const [fpNewPass, setFpNewPass] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [fpError, setFpError] = useState("");

  const resetFp = () => { setFpStep(1); setFpUser(""); setFpQuestion(""); setFpAnswer(""); setFpNewPass(""); setFpError(""); setFpBusy(false); };

  const fpGetQuestion = async () => {
    if (!fpUser.trim()) { setFpError("Masukkan username Anda"); return; }
    setFpBusy(true); setFpError("");
    try {
      const r = await api.post("/auth/forgot-password/question", { username: fpUser.trim() });
      setFpQuestion(r.data.security_question);
      setFpStep(2);
    } catch (err) { setFpError(formatApiError(err.response?.data?.detail) || "Gagal memuat pertanyaan"); }
    finally { setFpBusy(false); }
  };

  const fpSubmit = async () => {
    if (!fpAnswer.trim()) { setFpError("Isi jawaban keamanan"); return; }
    if (fpNewPass.length < 6) { setFpError("Password baru minimal 6 karakter"); return; }
    setFpBusy(true); setFpError("");
    try {
      await api.post("/auth/forgot-password/reset", { username: fpUser.trim(), answer: fpAnswer.trim(), new_password: fpNewPass });
      toast.success("Password berhasil diubah. Silakan masuk dengan password baru.");
      setFpOpen(false); resetFp();
    } catch (err) { setFpError(formatApiError(err.response?.data?.detail) || "Gagal mengubah password"); }
    finally { setFpBusy(false); }
  };

  const redirectFor = (role) =>
    role === "supplier" ? "/portal" : role === "karyawan" ? "/transactions" : "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username, password);
      navigate(redirectFor(u.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden" style={{ backgroundImage: "url('https://customer-assets-jai6qajn.emergentagent.net/job_business-ledger-540/artifacts/ad286e93d6f59167_lala%20dekstop.jpeg')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1633]/85 via-[#12306b]/70 to-[#0A1633]/85" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#C9A227]/15 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-16 w-16 rounded-2xl bg-white border-2 border-[#C9A227]/80 overflow-hidden shadow-lg">
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_business-ledger-540/artifacts/e4f82042f591207e_WhatsApp%20Image%202026-10-07%20at%2015.59.58.jpeg" alt="Logo Lala" className="h-full w-full object-cover" />
          </div>
          <span className="font-heading font-extrabold text-xl text-[#E8C766]">Lala Corporation Indonesia</span>
        </div>
        <div className="relative" />
        <div className="relative text-sm text-blue-100/60">© 2019 Lala Corporation Indonesia.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-xl bg-white border border-[#C9A227]/70 overflow-hidden">
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_business-ledger-540/artifacts/e4f82042f591207e_WhatsApp%20Image%202026-10-07%20at%2015.59.58.jpeg" alt="Logo Lala" className="h-full w-full object-cover" />
            </div>
            <span className="font-heading font-extrabold text-lg">Lala Corporation Indonesia</span>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Masuk ke akun Anda</h1>
          <p className="text-sm text-muted-foreground mt-1">Gunakan username dan password Anda.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="cth. owner"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive font-medium">{error}</p>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit" variant="gold" className="w-full h-11 text-sm font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { resetFp(); setFpOpen(true); }} className="w-full text-center text-xs text-muted-foreground hover:text-primary underline underline-offset-2 mt-1">
              Lupa password?
            </button>
          </form>

          <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-[11px] uppercase tracking-wider"><span className="bg-background px-2 text-muted-foreground">atau</span></div>
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="login-google"
              className="w-full h-11 mt-4 text-sm font-semibold gap-2"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
              </svg>
              Masuk dengan Google
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2 text-center">Hanya untuk akun Owner/Admin yang emailnya sudah didaftarkan Owner.</p>
          </div>

        </div>
      </div>

      <Dialog open={fpOpen} onOpenChange={(o) => { setFpOpen(o); if (!o) resetFp(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Lupa Password</DialogTitle>
            <DialogDescription className="text-xs">
              {fpStep === 1
                ? "Masukkan username Anda untuk menampilkan pertanyaan keamanan."
                : "Jawab pertanyaan keamanan lalu buat password baru. Jika lupa jawaban, hubungi Owner untuk reset manual."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {fpStep === 1 ? (
              <div className="space-y-1.5"><Label className="text-xs">Username</Label><Input data-testid="fp-username" className="h-9 text-sm" value={fpUser} onChange={(e) => setFpUser(e.target.value)} placeholder="cth. budi" /></div>
            ) : (
              <>
                <div className="rounded-lg bg-accent/40 p-2.5 text-xs" data-testid="fp-question"><span className="text-muted-foreground">Pertanyaan: </span><span className="font-medium">{fpQuestion}</span></div>
                <div className="space-y-1.5"><Label className="text-xs">Jawaban Keamanan</Label><Input data-testid="fp-answer" className="h-9 text-sm" value={fpAnswer} onChange={(e) => setFpAnswer(e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Password Baru</Label><Input data-testid="fp-new-password" type="text" className="h-9 text-sm" value={fpNewPass} onChange={(e) => setFpNewPass(e.target.value)} placeholder="Min. 6 karakter" /></div>
              </>
            )}
            {fpError && <p data-testid="fp-error" className="text-xs text-destructive font-medium">{fpError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => { setFpOpen(false); resetFp(); }}>Batal</Button>
            {fpStep === 1 ? (
              <Button onClick={fpGetQuestion} disabled={fpBusy} data-testid="fp-next" variant="gold" size="sm" className="h-9 text-xs">{fpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lanjut"}</Button>
            ) : (
              <Button onClick={fpSubmit} disabled={fpBusy} data-testid="fp-submit" variant="gold" size="sm" className="h-9 text-xs">{fpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ubah Password"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
